import React, { useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  Typography
} from '@material-ui/core';
import { setImportDupCheckErrors, revalidateImportRows, setImportGridEditDirty } from 'store/actions';
import { importTypeStateSelector } from 'store/selectors';
import classNames from 'classnames';

import DataGrid, {
  Column,
  Pager,
  Paging,
  Sorting,
  Scrolling,
  ColumnFixing,
  Editing,
  KeyboardNavigation,
  Lookup,
  Selection
} from 'devextreme-react/data-grid';
import { Template } from 'devextreme-react/core/template';
import { Constants } from 'utilities/constants';
import { columnSchema } from './gridConfig';

const ImportTasksGrid = React.forwardRef(({ classes, importRows, dupCheckErrors,
  setImportDupCheckErrors, revalidateImportRows, setImportGridEditDirty }, gridRef) => {
  const dupRowReferenceIds = dupCheckErrors?.map(error => error.referenceId) || [];
  const hasErrors = importRows.some(row => row.invalidColumns?.length > 0) || dupRowReferenceIds.length;
  const maxCountExceeded = importRows?.length > Constants.ImportMaxCount.POSTACQTASKS;

  useEffect(() => {
    const dataSource = gridRef.current.instance.getDataSource();
    if (!(dataSource && importRows)) return;
    if (gridRef.current.instance.hasEditData()) {
      gridRef.current.instance.cancelEditData();
    }
  }, [importRows])

  const onFocusedCellChanging = e => {
    e.isHighlighted = true;
  };

  // A value pasted from SQL as the literal text 'NULL' (or any other non-numeric string) lands in a
  // numeric cell as a string the dxNumberBox editor can't represent: it renders empty, so a clear fires
  // no change event, batch editing records nothing, and the stuck 'NULL' survives the save (hence the
  // "type a number, save, clear, save again" workaround users hit). For these cells, swap the editor to a
  // text box so the invalid value stays visible until the user manually clears (backspaces) it. On change
  // we coerce blank -> null and numeric text -> number, so the edit registers and the validation error
  // resolves on save. Cells holding a valid number (or null) keep the normal numeric editor.
  const onEditorPreparing = e => {
    if (e.parentType !== 'dataRow' || e.dataType !== 'number') return;
    const raw = e.value;
    const isNonNumericString = typeof raw === 'string'
      && raw.trim() !== ''
      && isNaN(Number(raw.replace(/[\s$,]+/g, '')));
    if (!isNonNumericString) return;

    e.editorName = 'dxTextBox'; // let the un-representable value display and be cleared manually
    e.editorOptions.value = raw;
    e.editorOptions.onValueChanged = args => {
      const next = args.value;
      if (next == null || next.toString().trim() === '') {
        e.setValue(null);
      } else {
        const parsed = Number(next.toString().replace(/[\s$,]+/g, ''));
        e.setValue(isNaN(parsed) ? next : parsed); // numeric text -> number; otherwise keep so it still fails validation
      }
    };
  };

  const onContentReady = e => {
    setImportGridEditDirty(gridRef.current.instance.hasEditData());
    if (!importRows || !hasErrors) return;

    importRows.forEach((row, index) => {
      const rowIndex = gridRef.current.instance.getRowIndexByKey(row.referenceId);
      if (rowIndex === -1) return;

      if (dupRowReferenceIds?.includes(row.referenceId)) { // Mark rows that are dups
        const rowEl = gridRef.current.instance.getRowElement(rowIndex);
        if (rowEl?.[0]) {
          rowEl[0].classList.add('invalidCell');
        }
        if (rowEl?.[1]) {
          rowEl[1].classList.add('invalidCell');
        }
      }

      if (row.invalidColumns?.length) {
        row.invalidColumns.forEach(col => {
          let fields;
          if (col.name === 'address') {
            fields = ['street', 'city', 'state', 'zipCode'];
          } else {
            fields = [col.name]
          }
          fields.forEach(field => {
            const cell = gridRef.current.instance.getCellElement(rowIndex, field);
            if (cell) {
              cell.classList.add('invalidCell');
            }
          })
        })
      }
    })
  }

  function getHeaderCell(el, headerStyle) {
    return (
      <div>
        <span>{el.data.column.caption}</span>
        {headerStyle.required &&
          <span className={classes.requiredField}>*</span>
        }
      </div>
    )
  }

  const onToolbarPreparing = e => {
    e.toolbarOptions.items.unshift(
      {
        location: 'before',
        template: 'totalCount'
      },
      {
        location: 'before',
        template: 'excludedCount'
      },
      {
        location: 'before',
        template: 'columnKey'
      },
    );

    const saveButton = e.toolbarOptions.items.find(el => el.name === 'saveButton');
    if (saveButton) {
      saveButton.options.onClick = handleSaveEdit;
    }
  }

  const toolbarItemRender = (e) => {
    switch (e.template) {
      case 'totalCount':
        return totalCountTemplate();
      case 'excludedCount':
        return excludedCountTemplate();
      case 'columnKey':
        return columnKeyTemplate();
      default:
        return;
    }
  }

  const totalCountTemplate = (e) => {
    return (<div className={classNames(classes.informer, maxCountExceeded && classes.errorInformer)}>
      <span className={classes.countLabel}>{importRows?.length ?? 0}</span>
      <span className={classes.totalCountText}>Total Count</span>
    </div>)
  }

  const excludedCountTemplate = (e) => {
    const totalCount = gridRef?.current?.instance?.totalCount();
    const filter = gridRef?.current?.instance?.getCombinedFilter();
    return totalCount && filter
      ? <div className={classNames(classes.informer, classes.exclusionInformer)}>
        <span className={classes.countLabel}>{totalCount > -1 ? totalCount : 0}</span>
        <span className={classes.totalCountText}>Visible</span>
      </div>
      : null
  }

  const columnKeyTemplate = (e) => {
    return (<div style={{ padding: 4 }}>
      <Typography variant='body2'><span style={{ color: 'red' }}>*</span> Required field</Typography>
    </div>)
  }

  const handleSaveEdit = () => {
    const dupFound = dupCheckErrors?.length > 0;
    gridRef.current.instance.saveEditData().then(() => {
      const dataSource = gridRef.current.instance.getDataSource();
      gridRef.current.instance.beginUpdate();
      gridRef.current.instance.clearFilter();
      setTimeout(() => { // Important - as grid internally rerenders from above clearFilter(), items() will return empty array. Add delay here to get correct items array.
        const items = [...dataSource.items()];
        if (dupFound) {
          checkForDeleted(items);
        }
        revalidateImportRows(items);
        gridRef.current.instance.endUpdate();
      }, 100);
    });
  }

  const checkForDeleted = (dataSourceItems) => {
    if (importRows?.length === dataSourceItems?.length) return;
    const deletedRows = importRows.filter(oldRow => !dataSourceItems.find(newRow => newRow.referenceId === oldRow.referenceId));
    if (!deletedRows.length) return;

    const deletedKeys = deletedRows.map(row => row.referenceId);

    const newDupCheckErrors = dupCheckErrors.filter(oldError => !deletedKeys.includes(oldError.referenceId));
    setImportDupCheckErrors(newDupCheckErrors);
  }

  return (
    <DataGrid
      ref={gridRef}
      dataSource={importRows.map(row => { return { ...row } })} // Create array of new rows, else deleting rows/editing cells will mutate the state
      keyExpr='referenceId'
      allowColumnResizing={true}
      columnResizingMode='widget'
      showBorders={true}
      showRowLines={true}
      showColumnLines={true}
      height={'100%'}
      focusedRowEnabled={false}
      // loadPanel={{ enabled: false }}
      onContentReady={onContentReady}
      onFocusedCellChanging={onFocusedCellChanging}
      onEditorPreparing={onEditorPreparing}
      onToolbarPreparing={onToolbarPreparing}
    >
      <Template name='totalCount' render={toolbarItemRender} />
      <Template name='excludedCount' render={toolbarItemRender} />
      <Template name='columnKey' render={toolbarItemRender} />
      <Editing
        mode='batch'
        allowUpdating={true}
        allowDeleting={true}
        // selectTextOnEditStart={true}
        startEditAction='dblClick'
      />
      <KeyboardNavigation
        editOnKeyPress={true}
        enterKeyAction='startEdit'
        enterKeyDirection={'column'}
      />
      <ColumnFixing enable={true} />
      <Selection mode={'multiple'} showCheckBoxesMode={false} />
      <Sorting mode='none' />
      <Scrolling rowRenderingMode='virtual' columnRenderingMode='virtual'/>
      <Paging
        defaultPageSize={100}
        enabled={false}
      />
      <Pager showInfo={true} />
      {columnSchema.map(({ field, displayName, width, type, format, fixed, required, acceptedValues }) => {
        const headerStyle = {
          required
        }
        let lookupDataSource = null;
        if (type === 'boolean') {
          lookupDataSource = [{ displayExpr: 'true', valueExpr: true }, { displayExpr: 'false', valueExpr: false }];
        } else if (!!acceptedValues) {
          lookupDataSource = acceptedValues.map(value => {
            return {
              displayExpr: value,
              valueExpr: value
            }
          });
        }
        if (lookupDataSource && !required) { // Add in null lookup option if field not required
          lookupDataSource.unshift({ displayExpr: '', valueExpr: null });
        }
        return (
          <Column
            key={field}
            dataField={field}
            caption={displayName}
            fixed={fixed || false}
            minWidth={width}
            dataType={type === 'date' ? 'string' : type} // display dates as strings (cell editing has errors when using dates)
            format={format}
            allowExporting={true}
            visible={true}
            headerCellComponent={headerStyle ? (el) => getHeaderCell(el, headerStyle) : undefined}
            // visibleIndex={index}
            showEditorAlways={type === 'boolean' || !!acceptedValues}
          >
            {!!lookupDataSource &&
              <Lookup dataSource={lookupDataSource} displayExpr="displayExpr" valueExpr="valueExpr" />
            }
          </Column>
        )
      })}
    </DataGrid>
  )
})

const styles = theme => ({
  requiredField: {
    color: 'red'
  },
  informer: {
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    width: '130px',
    background: '#80a4c7',
    color: '#fff',
    textAlign: 'center'
  },
  exclusionInformer: {
    background: '#67c12e',
  },
  errorInformer: {
    background: '#d32f2f'
  },
  countLabel: {
    fontSize: '18px',
    paddingTop: '5px',
    margin: '0'
  },
  totalCountText: {
    paddingBottom: '5px',
  },
});

const mapStateToProps = state => {
  const importState = importTypeStateSelector(state, Constants.ImportType.POSTACQTASKS);
  return {
    importRows: importState.importRows,
    dupCheckErrors: importState.importDupCheckErrors
  }
}
const mapDispatchToProps = dispatch => {
  const importType = Constants.ImportType.POSTACQTASKS;
  return {
    setImportDupCheckErrors: (dupCheckErrors) => dispatch(setImportDupCheckErrors({ importType, dupCheckErrors })),
    revalidateImportRows: (rows) => dispatch(revalidateImportRows({ importType, rows })),
    setImportGridEditDirty: (gridEditDirty) => dispatch(setImportGridEditDirty({ importType, gridEditDirty })),
  }
};
export default compose(withStyles(styles), connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
))(ImportTasksGrid);
